import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button, Space, Table, Tag, Typography } from "antd";
import { PlusOutlined } from "@ant-design/icons";
import { useDispatch, useSelector } from "react-redux";
import { listSequences } from "../../../redux/actions/sequencesAction";
import useTableScrollY from "../../../hooks/useTableScrollY";
import { TRIGGER_TYPE_OPTIONS } from "./sequenceUtils";

const { Title, Text } = Typography;

const STATUS_COLORS = {
  draft: "default",
  active: "green",
  paused: "orange",
  archived: "default",
};

const TRIGGER_LABELS = TRIGGER_TYPE_OPTIONS.reduce((acc, opt) => {
  acc[opt.value] = opt.label.replace(" (coming soon)", "");
  return acc;
}, {});

export default function SequencesList() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const sequencesList = useSelector((state) => state.sequences.sequences);
  const [loading, setLoading] = useState(false);
  const [tableAreaRef, tableScrollY] = useTableScrollY([
    loading,
    sequencesList.length,
  ]);

  const fetchSequences = useCallback(() => {
    setLoading(true);
    dispatch(listSequences({}))
      .then(() => setLoading(false))
      .catch(() => setLoading(false));
  }, [dispatch]);

  useEffect(() => {
    fetchSequences();
  }, [fetchSequences]);

  const columns = [
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      render: (name, record) => (
        <Button type="link" onClick={() => navigate(`/app/sequences/${record._id}`)}>
          {name}
        </Button>
      ),
    },
    {
      title: "Trigger",
      dataIndex: "trigger",
      key: "trigger",
      render: (trigger) => (
        <Tag>{TRIGGER_LABELS[trigger?.type] || trigger?.type}</Tag>
      ),
    },
    {
      title: "Steps",
      dataIndex: "steps",
      key: "steps",
      render: (steps) => steps?.length || 0,
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status) => (
        <Tag color={STATUS_COLORS[status] || "default"}>{status}</Tag>
      ),
    },
  ];

  return (
    <div className="Sequences">
      <Space
        wrap
        style={{
          display: "flex",
          margin: "0 0.5rem 0.25rem 0.5rem",
          justifyContent: "space-between",
        }}
      >
        <div>
          <Title level={5} style={{ marginBottom: 0 }}>
            Sequences
          </Title>
          <Text type="secondary">
            Automatically send a series of emails to contacts when they submit a
            form, confirm opt-in, or get tagged.
          </Text>
        </div>
        <Space size="small">
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => navigate("/app/sequences/new")}
          >
            New sequence
          </Button>
        </Space>
      </Space>

      {sequencesList.length === 0 && !loading ? (
        <Text type="secondary" style={{ display: "block", margin: "0 0.5rem" }}>
          No sequences yet. Create one to start automating follow-up emails.
        </Text>
      ) : (
        <div ref={tableAreaRef} style={{ width: "100%" }}>
          <Table
            rowKey="_id"
            loading={loading}
            columns={columns}
            dataSource={sequencesList}
            scroll={{
              x: 800,
              y: tableScrollY,
            }}
            pagination={false}
          />
        </div>
      )}
    </div>
  );
}
